import { combineReducers, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { uniqBy } from "lodash";
import { getUserHltId } from "utils/CommonFunctions";
import { responseTypes } from "utils/Types";
import { axiosDelete, axiosGet, axiosPost } from "../../../utils/APIHelper";
import { updatePlanAsync } from "../MyPlanDetails/MyPlanDetails.slice";
import { Plan, PlanState } from "./plans";

const initialState: PlanState = {
  list: [],
  totalRecords: 0,
  status: "idle",
  filters: [],
  innCodes: [],
  brands: [],
  adminInncodes: [],
  ownerDetails: [],
  myPlanShowLoader: false,
  selectedAdminInncodes: [],
  innCodeDetails: [],
  isActionHighlight: false
};

export const fetchOwnerDetailsAsync = createAsyncThunk(`ownerDetails`, async (data: any, thunkApi) => {
  const API_URL = `associates?size=${data.size}&hltIds=${data.value}`;
  const response: any = await axiosGet(API_URL, "user");
  return response.data;
});

export const fetchMyPlansAsync = createAsyncThunk(`myplans`, async (data: {}, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const API_URL = `plans`;
  const response: any = await axiosGet(API_URL, "userplan", data);
  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  }
  return response.data;
});

export const assignPlanAsync = createAsyncThunk(`assignPlan`, async (data: {}, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const API_URL = `/plan-assignment`;
  const response: any = await axiosPost(API_URL, "userplan", data);
  if (response?.status === responseTypes.accepted) {
    return response;
  } else {
    return rejectWithValue(response.response.data);
  }
});

export const deleteMyPlansAsync = createAsyncThunk(`deletemyplans`, async (data: any, thunkApi) => {
  const { fulfillWithValue, rejectWithValue } = thunkApi;
  const API_URL = `plans/${data.id}`;
  const response = await axiosDelete(API_URL, "userplan");
  if (response.status === responseTypes.no_content) {
    return fulfillWithValue(data);
  } else {
    return rejectWithValue(response.response.data);
  }
});

export const fetchMyPlansWithInncodesAsync = createAsyncThunk(`myplans_inncodes`, async (data: {}, thunkApi) => {
  const API_URL = `plans`;

  const response = await axiosGet(API_URL, "userplan", { ...data, searchedHltId: getUserHltId() });

  return response.data.userplans;
});

export const fetchAdminInncodesAsync = createAsyncThunk(`admin_inncodes`, async (data: {}, thunkApi) => {
  const API_URL = `innCodes?searchText=${data}`;

  const response = await axiosGet(API_URL, "");

  return response.data;
});

export const MyPlansSlice = createSlice({
  name: "myplans",
  initialState,
  reducers: {
    clearAdminInncodes: (state) => {
      state.adminInncodes = [];
    },
    setInncodeDetails: (state, action) => {
      state.innCodeDetails = action.payload;
    },
    clearMyPlanList: (state) => {
      state.list = [];
    },
    setSelectedAdminInncodes: (state, action) => {
      state.selectedAdminInncodes = action.payload;
    },
    setIsPlanHighlight: (state, action) => {
      state.isActionHighlight = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyPlansAsync.pending, (state, action: any) => {
        state.myPlanShowLoader = action.meta.arg.page === 0;
        state.status = "loading";
      })
      .addCase(fetchMyPlansAsync.fulfilled, (state, action: any) => {
        state.myPlanShowLoader = false;
        state.status = "idle";
        state.list =
          action.meta.arg.page === 0 ? action.payload.userplans : [...state.list, ...action.payload.userplans];
        state.totalRecords = action.payload.meta?.page?.total;
        state.filters = action.payload.meta?.filters;
        state.innCodes = action.payload.meta?.filters.map((item: any) => item.innCode);
        state.brands = uniqBy(
          action.payload.meta?.filters.map((item: any) => item),
          "brandCode"
        );
      })
      .addCase(fetchMyPlansAsync.rejected, (state, action: any) => {
        state.myPlanShowLoader = false;
        state.status = "idle";
      })
      .addCase(fetchOwnerDetailsAsync.fulfilled, (state, action: any) => {
        state.myPlanShowLoader = false;
        state.ownerDetails = action.payload?.users;
      })
      .addCase(fetchAdminInncodesAsync.fulfilled, (state, action: any) => {
        state.status = "idle";
        state.adminInncodes = action.payload.properties;
      })
      .addCase(deleteMyPlansAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteMyPlansAsync.fulfilled, (state, action: any) => {
        state.status = "idle";
        state.list = state.list.filter((plan) => plan.id !== action?.meta?.arg?.id);
      })
      .addCase(updatePlanAsync.fulfilled, (state, action: any) => {
        const list = [...state.list];
        state.list = list.map((element) => {
          if (element.id === action.payload.id) {
            element = { ...element, ...action.payload, title: action.payload.name };
          }
          return element;
        });
      })
      .addCase(assignPlanAsync.pending, (state) => {
        state.myPlanShowLoader = true;
      })
      .addCase(assignPlanAsync.fulfilled, (state, action: any) => {
        state.myPlanShowLoader = false;
      })
      .addCase(assignPlanAsync.rejected, (state) => {
        state.myPlanShowLoader = false;
      });
  }
});
export const MyPlansWithInncodesSlice = createSlice({
  name: "myplanswithinncodes",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyPlansWithInncodesAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchMyPlansWithInncodesAsync.fulfilled, (state, action: PayloadAction<Plan[]>) => {
        state.status = "idle";
        state.list = action.payload;
      })
      .addCase(fetchMyPlansWithInncodesAsync.rejected, (state) => {
        state.status = "failed";
      });
  }
});
const rootReducer = combineReducers({
  MyPlans: MyPlansSlice.reducer,
  MyPlansWithInncodes: MyPlansWithInncodesSlice.reducer
});

export const { clearAdminInncodes, clearMyPlanList, setSelectedAdminInncodes, setInncodeDetails, setIsPlanHighlight } =
  MyPlansSlice.actions;

export default rootReducer;
